// components/common/search-result/UserSearchResultItem.tsx
"use client";

import React from "react";
import { useRouter } from "next/navigation";
import {
  MessageCircleMore,
  UsersRound,
  UserRoundCheck,
  UserRoundX,
  UserPlus2,
  UserMinus2,
} from "lucide-react";
import { FriendshipUserType } from "../../../types/network/friendshipTypes";

import { AvatarDisplay } from "../index";
import "./UserSearchResultItem.css";


interface Props {
  user: FriendshipUserType;
  showFriendActions?: boolean;
  onSendMessage?: (userId: number) => void;
  onAcceptRequest?: (friendshipId: number) => void;
  onDeclineRequest?: (friendshipId: number) => void;
  onCancelRequest?: (friendshipId: number) => void;
  onSendRequest?: (userId: number) => void;
  onAddToGroup?: (userId: number) => void;
}

const UserSearchResultItem: React.FC<Props> = ({
  user,
  showFriendActions = false,
  onSendMessage,
  onAcceptRequest,
  onDeclineRequest,
  onCancelRequest,
  onSendRequest,
  onAddToGroup
}) => {
  const router = useRouter();

  const handleProfileClick = () => {
    if (user.profile_url) router.push(user.profile_url);
  };

  return (
    <li className="search-result-item">
      <AvatarDisplay
        userId={user.id}
        src={user.avatar_cdn_url || null}
        placeholderSrc={user.avatar_url || user.profile_image || null}
        alt={`${user.name || ""} ${user.family || ""}`}
        shape="rounded"
        size="md"
        onClick={handleProfileClick}
        withBorder
        borderColor={user.label?.color}
        isVerifiedIdentity={user.is_verified_identity}
        isTownlitVerified={user.is_townlit_verified}
      />

      <div className="friend-info">
        <div className="friend-info-contain" onClick={handleProfileClick} title="Go to Porfile">
          <div className="friend-info-name">
            <span>{user.name}</span>&nbsp;<span>{user.family}</span>
          </div>
          <div className="friend-info-username">
            <span>@{user.username}</span>
          </div>
        </div>

        <div className="friend-action-buttons">

          {/* Show "Add to group" if provided (group flow) */}
          {onAddToGroup && (
            <button
              className="friend-plus-button"
              title="Add to Group"
              onClick={() => onAddToGroup(user.id)}
            >
              <UserPlus2 size={18} />
            </button>
          )}

          {/* Show message button only if onSendMessage is provided */}
          {onSendMessage && (
            <button
              className="friend-message-button"
              title="Send Message"
              onClick={() => onSendMessage(user.id)}
            >
              <MessageCircleMore size={18} />
            </button>
          )}

          {showFriendActions && (
            <>
              {user.is_friend ? (
                <button
                  className="friend-status"
                  title="Go to Profile"
                  onClick={handleProfileClick}
                >
                  <UsersRound size={18} />
                </button>
              ) : user.has_received_request ? (
                <>
                  <button
                    className="friend-accept-button"
                    title="Accept Friend Request"
                    onClick={() => onAcceptRequest?.(user.friendship_id!)}
                  >
                    <UserRoundCheck size={18} />
                  </button>
                  <button
                    className="friend-decline-button"
                    title="Decline Friend Request"
                    onClick={() => onDeclineRequest?.(user.friendship_id!)}
                  >
                    <UserRoundX size={18} />
                  </button>
                </>
              ) : user.request_sent ? (
                <button
                  className="friend-minus-button"
                  title="Cancel Friend Request"
                  onClick={() => onCancelRequest?.(user.friendship_id!)}
                >
                  <UserMinus2 size={18} />
                </button>
              ) : (
                <button
                  className="friend-plus-button"
                  title="Send Friend Request"
                  onClick={() => onSendRequest?.(user.id)}
                >
                  <UserPlus2 size={18} />
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </li>
  );
};

export default UserSearchResultItem;
